import type { ReactNode } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import type { ConsultationQuestionOptions } from '../types/consultationQuestion';

type OptionsDescriptions = {
  always: string;
  between: string;
  never: string;
  sometimes: string;
};

type OptionsSummaryProps = {
  children?: ReactNode;
  descriptions: OptionsDescriptions;
  label: string;
  value?: string;
};

/**
 * @Eric: Show a summary sentence of the user choice. This sentence is computed from the "what" (diet type) and the "when" (frequency).
 * The most specific piece of this component, would have to be modified is a reuse of radio group is considered.
 * Only shown on tablet / desktop.
 */
const OptionsSummary = ({ children, descriptions, label, value }: OptionsSummaryProps) => {
  // @Eric: if no value return a hidden dummy sentence to avoid a layout shift when a value is selected
  if (!value) {
    return (
      <Typography style={{ visibility: 'hidden' }} variant="p1" whiteSpace="nowrap">
        I eat {label}...
      </Typography>
    );
  }

  const parse = (str: string) => str.replace('__what__', label).split('__when__');
  let content;

  switch (value) {
    case 'never': {
      const { never: description } = descriptions;
      content = parse(description);
      break;
    }
    case 'between_never_often': {
      const { sometimes: description } = descriptions;
      content = parse(description);
      break;
    }
    case 'every_day': {
      const { always: description } = descriptions;
      content = parse(description);
      break;
    }
    default: {
      const { between: description } = descriptions;
      content = parse(description);
    }
  }

  // @Eric: content length is min 1 and max 2
  return (
    <Typography variant="p1" whiteSpace="nowrap">
      {content[0]}
      <Typography markupName="strong" medium variant="p1">
        &nbsp;{children}&nbsp;
      </Typography>
      {content[1]}
    </Typography>
  );
};

// @Eric: controls placement of title and makes sure a horizontal line is shown behind the radio inputs
const Root = styled.fieldset`
  display: grid;
  grid-template-areas:
    'title'
    'options';
  place-items: center;
  gap: ${legacyTheme.spacing.s16};
  width: 100%;
  /* reset default look */
  margin: 0;
  padding: 0;
  border: 0;
  /* @Eric: first child on its own row */
  & > * {
    grid-area: title;
  }
  /* other children on ther own row & superposed */
  & > * + * {
    grid-area: options;
  }

  & > *:last-child {
    display: none;
  }

  ${legacyTheme.breakpoints.up('md')} {
    /* @Eric: on desktop we wants to show the description */
    grid-template-areas:
      'title'
      'options'
      'summary';

    & > *:last-child {
      display: inline-flex;
      grid-area: summary;
      align-items: baseline;
      margin-top: ${legacyTheme.spacing.s32};
    }
  }
`;

const Title = styled(Typography)`
  font-size: ${legacyTheme.spacing.s16};
  font-weight: 600;
`;

// @Eric: esthetic line gives the visual illusion that radios are connected
const Line = styled.span`
  width: 100%;
  height: 1px;

  background-color: black;
`;

// @Eric: controls placement of a Radio input + label
const OptionsContainer = styled.div`
  position: relative;

  gap: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

// @Eric: defines how a radio + label pair should be placed
const Option = styled.div`
  --radio-size: ${legacyTheme.spacing.s32};
  --options-color: ${theme.colors.primary['200']};
  /* @Eric: needed for positioning input labels */
  position: relative;

  display: grid;
  grid-template-areas: 'radio';
  place-items: center;
  /* @Eric: set the currentcolor for the children */
  color: black;
  /* @Eric: basically we wants radio & label to be superposed */
  & > * {
    grid-area: radio;
  }
`;

const StyledInput = styled.input`
  /* @Eric: necessary for superposition with ::before pseudo element */
  display: grid;
  place-content: center;
  width: var(--radio-size);
  height: var(--radio-size);
  /* Not removed via appearance */
  margin: 0;
  /* @Eric:reset default look */
  appearance: none;
  /* @Eric: for iOS < 15 to remove gradient background */
  background-color: ${theme.colors.neutral['100']};
  border-radius: 50%;
  color: currentcolor;
  border: 1px solid currentcolor;

  &::before {
    --options-color: white;
    --inset-width: ${legacyTheme.spacing.s24};

    cursor: pointer;
    content: '';
    /* @Eric: initially the pseudo element is not showned */
    transform: scale(0);

    width: var(--inset-width);
    height: var(--inset-width);

    border-radius: 50%;
    /* color within the pseudo element. background-color would have sone the job too */
    box-shadow: inset 0 0 0 calc(var(--inset-width) / 2) var(--options-color);
    /* when the radio is checked, scaled is set to 1 which triggers this animation */
    transition:
      120ms transform,
      box-shadow ease-in-out;
  }
  /* @Eric: triggers the animation */
  &:checked::before {
    transform: scale(1);
  }

  ${legacyTheme.breakpoints.up('sm')} {
    /* @Eric: disable hover state on mobile as there is a bug where hover can stay on an element not being hovered anymore */
    &:hover::before {
      transform: scale(1);
    }
  }
  /* @Eric: a specific color for checked states */
  &:is(:checked, :checked:hover)::before {
    --options-color: ${theme.colors.primary[400]};
  }
  /* @Eric: another when being clicked. no effet on mobile */
  &:active::before {
    /* Vert SECONDARY-10 "dead in the water color" from our figma */
    --options-color: ${theme.colors.primary['200']};
  }
  /* @Eric: and a last one when hovered */
  &:hover::before {
    --options-color: ${theme.colors.primary[300]};
  }
`;

const Label = styled(Typography)`
  --label-gap: ${legacyTheme.spacing.s12};

  position: absolute;
  /* @Eric: placement below the radio + gap */
  top: calc(var(--radio-size) + var(--label-gap));

  cursor: pointer;
`;

type RadioGroupProps = {
  descriptions: OptionsDescriptions;
  name: string;
  onChange: (opt: { name: string; value: string | number }) => void;
  options: ConsultationQuestionOptions;
  selected?: string;
};

export const RadioGroup = ({
  descriptions,
  name,
  onChange,
  options,
  selected,
}: RadioGroupProps) => {
  const summaryLabel = options.find((o) => o.value === selected)?.summaryLabel ?? null;

  return (
    <Root>
      <span>
        <Title markupName="legend" variant="mono1">
          {name}
        </Title>
      </span>
      <Line />
      <OptionsContainer>
        {options.map((opt) => (
          <Option key={opt.value}>
            <StyledInput
              checked={selected === opt.value}
              data-testid={`${name}-${opt.value}`}
              id={`${name}-${opt.value}`}
              name={name}
              onChange={(e) => {
                onChange({ name, value: e.target.value });
              }}
              type="radio"
              value={opt.value}
            />
            <Label
              htmlFor={`${name}-${opt.value}`}
              markupName="label"
              variant="p4"
              whiteSpace="nowrap"
            >
              {opt.label}
            </Label>
          </Option>
        ))}
      </OptionsContainer>
      <OptionsSummary descriptions={descriptions} label={name} value={selected}>
        {summaryLabel}
      </OptionsSummary>
    </Root>
  );
};
